import { hasLowerLevel, levelAt, worldToRadar, type MapId } from "@69ly/maps";
import { type CreateStratInput, type DemoComment, type DemoScope, type DemoDetail, type NotePin, type NotePinKind, type Replay, type Scope, type Side, type StratItem } from "@69ly/shared";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Select } from "../components/Select.tsx";
import { roundsOf, type HeatFilter } from "../demo/heatmap.ts";
import type { GrenadeMatch } from "../demo/matching.ts";
import { grenadesAt, playersAt, type RoundIndex } from "../demo/scene.ts";
import { useAddComment, useComments, useDeleteComment, useEditComment } from "../lib/demo-queries.ts";
import { errorText } from "../lib/errors.ts";
import { useCreateStrat } from "../lib/strat-queries.ts";
import { linkInputs, NO_ABOUT, NoteAboutFields, NoteTags, type NoteAbout } from "./NoteFields.tsx";

const clock = (s: number) => {
  const v = Math.max(0, Math.ceil(s));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

// ---------------------------------------------------------------- heatmap

/** The Heatmap tab: what to show on the map instead of the round. */
export function HeatmapPanel({ replay, round, filter, onChange, samples }: { replay: Replay; round: RoundIndex; filter: HeatFilter; onChange: (f: HeatFilter) => void; samples: number }) {
  const { t } = useTranslation();
  const set = (change: Partial<HeatFilter>) => onChange({ ...filter, ...change });
  const rounds = roundsOf(replay, filter, round.index);
  const seg = <K extends keyof HeatFilter>(key: K, values: readonly HeatFilter[K][], label: string) => (
    <div className="field">
      <span id={`heat-${key}`}>{label}</span>
      <div className="segmented segmented-small" role="group" aria-labelledby={`heat-${key}`}>
        {values.map((v) => (
          <button key={String(v)} type="button" className="seg" aria-pressed={filter[key] === v} onClick={() => set({ [key]: v } as Partial<HeatFilter>)}>
            {t(`demos.heat.${key}.${String(v)}`, { n: round.round.number })}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="demo-heat">
      {seg("what", ["presence", "deaths", "kills"] as const, t("demos.heat.whatLabel"))}
      {seg("rounds", ["this", "all", "first", "second"] as const, t("demos.heat.roundsLabel"))}
      {seg("side", ["both", "t", "ct"] as const, t("demos.heat.sideLabel"))}
      <div className="field">
        <span>{t("demos.heat.playerLabel")}</span>
        <Select
          label={t("demos.heat.playerLabel")}
          value={filter.player === null ? "" : String(filter.player)}
          placeholder={t("demos.heat.everyone")}
          groups={[{ options: [{ value: "", label: t("demos.heat.everyone") }, ...replay.players.map((p, i) => ({ value: String(i), label: p.name }))] }]}
          onChange={(v) => set({ player: v === "" ? null : Number(v) })}
        />
      </div>
      <p className="muted small" role="status">
        {samples === 0 ? t("demos.heat.empty") : t(`demos.heat.legend.${filter.what}`, { count: rounds.length })}
      </p>
      <div className="demo-heat-scale" aria-hidden="true">
        <span>{t("demos.heat.less")}</span>
        <span className="demo-heat-ramp" />
        <span>{t("demos.heat.more")}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- a moment as a strat board

/** A strat board of this moment: both teams where they stand and look, the smokes and fires up, linked to stuffs when they are. */
export function boardFromMoment(o: {
  replay: Replay;
  map: MapId;
  round: RoundIndex;
  tick: number;
  numbers: number[];
  matches: GrenadeMatch[];
  side: Side;
  title: string;
  phase: string;
  notes: string;
  scope: Scope;
}): CreateStratInput {
  const { replay, map, round, tick, numbers } = o;
  const levels = hasLowerLevel(map);
  const lvl = (z: number) => (levels ? levelAt(map, z) : "upper");
  const items: StratItem[] = [];
  for (const p of playersAt(replay, round, tick)) {
    if (!p.alive) continue;
    const at = worldToRadar(map, p.x, p.y);
    const rad = (p.yaw * Math.PI) / 180;
    const look = worldToRadar(map, p.x + Math.cos(rad) * 450, p.y + Math.sin(rad) * 450);
    items.push({ id: `p${p.index}`, kind: "token", level: lvl(p.z), side: p.side, n: numbers[p.index] ?? 1, u: at.u, v: at.v, look: [look.u, look.v], view: p.side === o.side });
  }
  const stuffOf = new Map(o.matches.filter((m) => m.stuff).map((m) => [m.grenade, m.stuff!.id]));
  grenadesAt(replay, round, tick).forEach((g, i) => {
    if (g.state !== "effect" || (g.kind !== "smoke" && g.kind !== "molotov")) return;
    const at = worldToRadar(map, g.x, g.y);
    items.push({ id: `g${i}`, kind: "grenade", level: lvl(g.z), grenade: g.kind, u: at.u, v: at.v, stuffId: stuffOf.get(g.grenade) ?? null });
  });
  return {
    scope: o.scope,
    map,
    side: o.side,
    title: o.title.slice(0, 60),
    notes: o.notes.slice(0, 1000),
    phases: [{ id: "p1", name: o.phase.slice(0, 30), items: items.slice(0, 300) }],
  };
}

// ---------------------------------------------------------------- notes

/**
 * The Notes tab: notes pinned to moments of this map (a click goes there), a new one at
 * the moment shown, and this moment opened as a strat board to draw on.
 */
/** A pin being placed on the map for a new note: its kind, the pin so far, and the click it waits for. */
export interface PinDraft {
  kind: NotePinKind | null;
  pin: NotePin | null;
  /** 0: the first click; 1: the second (where to look, the area's edge); 2: done. */
  step: number;
}
export const NO_PIN: PinDraft = { kind: null, pin: null, step: 0 };

export function NotesPanel({
  demo,
  replay,
  round,
  tick,
  makeBoard,
  onGo,
  draft,
  onDraft,
  canPick,
  teamNames,
}: {
  demo: DemoDetail;
  replay: Replay;
  round: RoundIndex;
  tick: number;
  /** Builds the strat board of this moment. */
  makeBoard: () => CreateStratInput;
  onGo: (roundIndex: number, seconds: number) => void;
  draft: PinDraft;
  onDraft: (d: PinDraft) => void;
  /** The 2D map is shown, so a click on it places the pin. */
  canPick: boolean;
  /** The replay's teams 0 and 1 as the match names them. */
  teamNames: [string, string];
}) {
  const { t } = useTranslation();
  const comments = useComments(demo.id);
  const add = useAddComment(demo.id);
  const del = useDeleteComment(demo.id);
  const createStrat = useCreateStrat();
  const edit = useEditComment(demo.id);
  const [text, setText] = useState("");
  const [about, setAbout] = useState<NoteAbout>(NO_ABOUT);
  const [editing, setEditing] = useState<string | null>(null);
  const scope: DemoScope | null = demo.match.pro ? "pro" : demo.match.teamId;
  const [error, setError] = useState<string | null>(null);
  // A note on a player is about their team and the side they play this round, unless chosen.
  const pinnedPlayer = draft.pin?.kind === "player" ? draft.pin.player : null;
  useEffect(() => {
    if (pinnedPlayer === null) return;
    const team = replay.players[pinnedPlayer]?.team;
    if (team === undefined) return;
    setAbout((a) => ({ ...a, team, side: team === round.round.tTeam ? "t" : "ct" }));
  }, [pinnedPlayer]); // eslint-disable-line react-hooks/exhaustive-deps
  const [board, setBoard] = useState<{ id: string } | null>(null);
  const seconds = (r: number, tk: number) => (tk - replay.rounds[r].startTick) / replay.tickRate;
  const here = t("demos.notes.at", { n: round.round.number, clock: clock(seconds(round.index, tick)) });
  const fail = (e: unknown) => setError(errorText(t, e));

  const openBoard = async (): Promise<string | null> => {
    try {
      const strat = await createStrat.mutateAsync(makeBoard());
      setBoard({ id: strat.id });
      return strat.id;
    } catch (e) {
      fail(e);
      return null;
    }
  };

  const submit = async (e: FormEvent, withBoard: boolean) => {
    e.preventDefault();
    setError(null);
    if (!text.trim()) return;
    const stratId = withBoard ? await openBoard() : null;
    if (withBoard && !stratId) return;
    const pin = draft.kind === "player" ? draft.pin : draft.step === 2 || draft.kind === "spot" ? draft.pin : null;
    add.mutate(
      {
        round: round.index,
        tick: Math.round(tick),
        text: text.trim(),
        stratId,
        pin,
        seconds: Math.round(((tick - round.round.startTick) / replay.tickRate) * 10) / 10,
        side: about.side,
        team: about.team,
        links: about.links,
      },
      {
        onSuccess: () => {
          setText("");
          setAbout(NO_ABOUT);
          onDraft(NO_PIN);
        },
        onError: fail,
      },
    );
  };

  return (
    <div className="demo-notes">
      <form className="demo-note-form" onSubmit={(e) => void submit(e, false)}>
        <label className="field">
          <span>{t("demos.notes.new", { at: here })}</span>
          <textarea rows={2} maxLength={500} value={text} placeholder={t("demos.notes.placeholder")} onChange={(e) => setText(e.target.value)} />
        </label>
        <PinPicker replay={replay} round={round} tick={tick} draft={draft} onDraft={onDraft} canPick={canPick} />
        <NoteAboutFields about={about} onChange={setAbout} teamNames={teamNames} scope={scope} />
        <div className="row">
          <button type="submit" className="btn btn-primary btn-small" disabled={!text.trim() || add.isPending}>
            {t("demos.notes.add")}
          </button>
          <button type="button" className="btn btn-secondary btn-small" disabled={!text.trim() || add.isPending || createStrat.isPending} onClick={(e) => void submit(e as unknown as FormEvent, true)}>
            {t("demos.notes.addWithBoard")}
          </button>
        </div>
      </form>
      <div className="demo-board-row">
        <button type="button" className="btn btn-quiet btn-small" disabled={createStrat.isPending} onClick={() => void openBoard()}>
          {t("demos.notes.board")}
        </button>
        {board && (
          <Link to={`/library/${replay.map}/strats?b=${board.id}`} className="small">
            {t("demos.notes.boardMade")}
          </Link>
        )}
      </div>
      {error && <p className="field-error small">{error}</p>}
      {comments.data && comments.data.length === 0 && <p className="muted small">{t("demos.notes.none")}</p>}
      <ol className="demo-note-list">
        {(comments.data ?? []).map((c) =>
          editing === c.id ? (
            <li key={c.id} className="is-editing">
              <NoteEditor
                note={c}
                teamNames={teamNames}
                scope={scope}
                saving={edit.isPending}
                onCancel={() => setEditing(null)}
                onSave={(change) => edit.mutate({ id: c.id, ...change }, { onSuccess: () => setEditing(null), onError: fail })}
              />
            </li>
          ) : (
            <li key={c.id} className={c.round === round.index ? "is-this-round" : ""}>
              <button type="button" className="demo-note-go" onClick={() => onGo(c.round, Math.max(0, seconds(c.round, c.tick)))}>
                <span className="mono small muted">{t("demos.notes.at", { n: replay.rounds[c.round]?.number ?? c.round + 1, clock: clock(seconds(c.round, c.tick)) })}</span>
                <span className="demo-note-text">{c.text}</span>
                {c.pin && (
                  <span className="chip chip-small demo-note-pin">{t(`demos.notes.pinned.${c.pin.kind}`, { name: c.pin.kind === "player" ? (replay.players[c.pin.player]?.name ?? "") : "" })}</span>
                )}
              </button>
              <NoteTags side={c.side} team={c.team} links={c.links} />
              <div className="demo-note-meta small muted">
                {c.author && <Avatar name={c.author.name} url={c.author.avatarUrl} size={18} />}
                <span>{c.author?.name ?? t("demos.notes.someone")}</span>
                {c.editedAt && <span title={new Date(c.editedAt).toLocaleString()}>{t("notes.edited")}</span>}
                {c.stratId && <Link to={`/library/${replay.map}/strats?b=${c.stratId}`}>{t("demos.notes.openBoard")}</Link>}
                {c.canEdit && (
                  <button type="button" className="link-button" onClick={() => setEditing(c.id)}>
                    {t("notes.edit")}
                  </button>
                )}
                {c.canDelete && (
                  <button type="button" className="link-button" onClick={() => del.mutate(c.id, { onError: fail })}>
                    {t("demos.notes.delete")}
                  </button>
                )}
              </div>
            </li>
          ),
        )}
      </ol>
    </div>
  );
}

/** A note being changed by its author: its text, side, team and links (its moment and pin stay). */
function NoteEditor({
  note,
  teamNames,
  scope,
  saving,
  onSave,
  onCancel,
}: {
  note: DemoComment;
  teamNames: [string, string];
  scope: DemoScope | null;
  saving: boolean;
  onSave: (change: { text: string; side: NoteAbout["side"]; team: NoteAbout["team"]; links: NoteAbout["links"] }) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(note.text);
  const [about, setAbout] = useState<NoteAbout>({ side: note.side, team: note.team?.index ?? null, links: linkInputs(note.links) });
  return (
    <form
      className="demo-note-form demo-note-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) onSave({ text: text.trim(), ...about });
      }}
    >
      <label className="field">
        <span>{t("notes.editLabel")}</span>
        <textarea rows={2} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      </label>
      <NoteAboutFields about={about} onChange={setAbout} teamNames={teamNames} scope={scope} />
      <div className="row">
        <button type="submit" className="btn btn-primary btn-small" disabled={!text.trim() || saving}>
          {t("notes.save")}
        </button>
        <button type="button" className="btn btn-quiet btn-small" onClick={onCancel}>
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}

/** What a new note points at: nothing, a spot (and where to look), a player or an area. */
/** A new note points at a spot or at a player (notes from before can also hold a view or an area). */
const NEW_PIN_KINDS = ["spot", "player"] as const;

function PinPicker({ replay, round, tick, draft, onDraft, canPick }: { replay: Replay; round: RoundIndex; tick: number; draft: PinDraft; onDraft: (d: PinDraft) => void; canPick: boolean }) {
  const { t } = useTranslation();
  const alive = playersAt(replay, round, tick).filter((p) => p.alive);
  const help =
    draft.kind === null || draft.kind === "player"
      ? null
      : !canPick
        ? t("demos.notes.pinIn2d")
        : draft.step === 0
          ? t(`demos.notes.click.${draft.kind}`)
          : draft.step === 1
            ? t(`demos.notes.clickNext.${draft.kind}`)
            : t("demos.notes.clickAgain");
  return (
    <div className="field demo-pin-picker">
      <span id="pin-kind">{t("demos.notes.pinLabel")}</span>
      <div className="segmented segmented-small" role="group" aria-labelledby="pin-kind">
        {[null, ...NEW_PIN_KINDS].map((k) => (
          <button key={k ?? "none"} type="button" className="seg" aria-pressed={draft.kind === k} onClick={() => onDraft({ kind: k, pin: null, step: 0 })}>
            {t(`demos.notes.pin.${k ?? "none"}`)}
          </button>
        ))}
      </div>
      {draft.kind === "player" && (
        <Select
          label={t("demos.notes.pinPlayer")}
          value={draft.pin?.kind === "player" ? String(draft.pin.player) : ""}
          placeholder={t("demos.notes.pinPlayer")}
          groups={[{ options: alive.map((p) => ({ value: String(p.index), label: p.name })) }]}
          onChange={(v) => onDraft({ kind: "player", pin: { kind: "player", player: Number(v), name: replay.players[Number(v)]?.name }, step: 2 })}
        />
      )}
      {help && (
        <p className="muted small" role="status">
          {help}
        </p>
      )}
    </div>
  );
}
